import * as React from "react";
import List from "./List";
import ListItem from "./ListItem";
import Box from "@sproutsocial/seeds-react-box";
import Loader from "@sproutsocial/seeds-react-loader";
import { useVirtualizer } from "@tanstack/react-virtual";
import type { TypeDataListProps } from "./DataListTypes";
import { useInView } from "react-intersection-observer";
import styled from "styled-components";

const HeaderContent = styled(Box)`
  width: 100%;
  transition: transform 0.4s ease-in-out;
  background-color: red;
`;

const Experimental_VirtualizedDataList = <T,>({
  as = "ul",
  data,
  renderItem,
  estimateItemSize = 50,
  onEndReached,
  hasNextPage,
  isFetchingNextPage,
  Header,
  removeHeaderDepth = Infinity,
  headerHeight = 60,
  ...rest
}: TypeDataListProps<T>) => {
  const afterInView = useInView();

  const parentRef = React.useRef<HTMLDivElement>(null);

  const count = data.length;

  const virtualizer = useVirtualizer({
    count,
    getScrollElement: () => parentRef.current,
    estimateSize: () => estimateItemSize,
    overscan: 5,
  });

  const items = virtualizer.getVirtualItems();
  const offset = virtualizer.scrollOffset ?? 0;

  const prevOffsetRef = React.useRef(offset);
  const [headerVisible, setHeaderVisible] = React.useState(true);

  React.useEffect(() => {
    const prev = prevOffsetRef.current;
    const scrollingUp = offset < prev;
    prevOffsetRef.current = offset;

    if (offset > removeHeaderDepth) {
      setHeaderVisible(scrollingUp);
    } else {
      setHeaderVisible(true);
    }
  }, [offset, removeHeaderDepth]);

  const transform = headerVisible
    ? "translateY(0px)"
    : `translateY(-${headerHeight}px)`;

  React.useEffect(() => {
    if (afterInView.inView) {
      onEndReached?.();
    }
  }, [onEndReached, afterInView.inView]);

  return (
    <div
      ref={parentRef}
      className="List"
      style={{
        width: "100%",
        height: "100%",
        overflowY: "auto",
        contain: "strict",
      }}
    >
      {Header && (
        <HeaderContent
          style={{
            position: "sticky",
            transform,
            top: 0,
            left: 0,
            width: "100%",
            zIndex: 1,
          }}
          id="header"
        >
          {Header}
        </HeaderContent>
      )}
      <List
        as={as}
        style={{
          height: `${virtualizer.getTotalSize()}px`,
          width: "100%",
          position: "relative",
        }}
      >
        {items.map((virtualRow) => {
          return (
            <ListItem
              key={virtualRow.key}
              data-index={virtualRow.index}
              ref={virtualizer.measureElement}
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                transform: `translateY(${
                  virtualRow.start - virtualizer.options.scrollMargin
                }px)`,
              }}
            >
              {renderItem(data[virtualRow.index] as T)}
            </ListItem>
          );
        })}
      </List>
      {hasNextPage && !isFetchingNextPage && (
        <div ref={afterInView.ref}>
          <Loader delay={false} />
        </div>
      )}
    </div>
  );
};

Experimental_VirtualizedDataList.displayName =
  "Experimental_VirtualizedDataList";

export default Experimental_VirtualizedDataList;
